import { useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useNavigationStore } from "presentation/mobile/stores/navigation-store";
import {
    useVacationPlanByFilter,
    useDeleteVacationPlanMutation,
    useUpdateVacationPlanMutation,
} from "presentation/hooks/use-query-vacation";
import { Vacation } from "domain/entities/vacations";
import { VacationInputFilter } from "domain/models/vacation-input-filter";
import { VacationStatusEnum } from "domain/enum/vacationStatus";
import VacationSkeleton from "./Skeleton";
import VacationCard from "./components/VacationCard";
import ScheduleVacationModal from "./components/ScheduleVacationModal";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import MobileFilterTabs from "presentation/mobile/components/MobileFilterTabs";
import { useStatusFilter } from "presentation/mobile/hooks/useStatusFilter";
import { VACATION_STATUS_FILTERS } from "presentation/mobile/utils/status-filters.util";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useEmployeesById } from "presentation/hooks/use-query-employees";
import MobileToast from "presentation/mobile/components/MobileToast";
import MobileLoadingOverlay from "presentation/mobile/components/MobileLoadingOverlay";
import MobileRefreshingIndicator from "presentation/mobile/components/MobileRefreshingIndicator";
import MobileRefreshingList from "presentation/mobile/components/MobileRefreshingList";

const FUNCTION = "vacations";
const REQUIRE_FUNCTION = "vacations";

const getVacationStatus = (vacation: Vacation) => vacation.status;

const Vacations = () => {
    const { isLoading: isCheckingPermission } = usePermissionCheck({
        requiredFunction: REQUIRE_FUNCTION,
    });

    const navigationState = useNavigationStore();
    const { userEmployee, setUserEmployee } = useAuthStore();
    const employeeId = userEmployee?.id;
    const toast = useRef<Toast>(null);

    //Dasos atualizados do colaborador na BD
    const { data: employeeData, refetch: refetchEmployeeData } = useEmployeesById(userEmployee?.id);

    //Parâmetro do máximo de dias a antecipar
    const {
        maxVacationDaysNextYear,
        setMaxVacationDaysNextYear } = useParameterStore();
    const { data: parameters } = useParameterByFilter({
        limit: 1000,
        sortBy: "value",
    } as BaseInputFilter);

    //Guarda o parâmetro no store global
    useEffect(() => {
        setMaxVacationDaysNextYear(
            parameters?.data?.filter((item) => item.type === "maxVacationDaysNextYear")
        );
    }, [parameters, setMaxVacationDaysNextYear]);

    //Sincroniza a sessão com os dados da BD
    useEffect(() => {
        const employee = employeeData?.data;
        if (!employee) return;

        setUserEmployee(employee);
    }, [employeeData, setUserEmployee]);

    const [localVacations, setLocalVacations] = useState<Vacation[]>([]);
    const [expandedVacationId, setExpandedVacationId] = useState<string | null>(null);
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    const filter: VacationInputFilter = {
        employeeId: employeeId ?? "",
        limit: 100,
    };

    const {
        data: vacationData,
        isLoading,
        isRefetching,
        refetch,
    } = useVacationPlanByFilter(filter);

    const { mutate: deleteVacation, isPending: isDeleting } = useDeleteVacationPlanMutation();
    const { mutate: updateVacation, isPending: isUpdating } = useUpdateVacationPlanMutation();

    useEffect(() => {
        if (navigationState.currentRoute !== FUNCTION) {
            navigationState.setCurrentRoute(FUNCTION);
        }
    }, [navigationState]);

    useEffect(() => {
        if (!vacationData?.data || isLoading) return;
        setLocalVacations(vacationData.data);
    }, [vacationData?.data, isLoading]);

    // Férias sempre ordenadas da mais recente para a mais antiga (por startAt)
    const sortedVacations = useMemo(
        () =>
            [...localVacations].sort(
                (a, b) => new Date(b.startAt ?? 0).getTime() - new Date(a.startAt ?? 0).getTime()
            ),
        [localVacations]
    );

    const {
        activeTab,
        setActiveTab,
        filterTabs,
        filteredItems: filteredVacations,
    } = useStatusFilter(sortedVacations, VACATION_STATUS_FILTERS, getVacationStatus);

    const confirmAction = (message: string, acceptCallback: () => void) => {
        confirmDialog({
            message,
            header: "Confirmação",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: acceptCallback,
        });
    };

    const handleCancel = (vacation: Vacation) => {
        const performCancel = () => {
            if (!vacation.id) return;

            if (vacation.status === VacationStatusEnum.A_APROVAR) {
                setLocalVacations((prev) => prev.filter((v) => v.id !== vacation.id));

                deleteVacation(vacation.id, {
                    onSuccess: async () => {
                        toast.current?.show({
                            severity: "success",
                            summary: "Cancelado",
                            detail: "Férias canceladas com sucesso!",
                            life: 3500,
                        });
                        await Promise.all([refetch(), refetchEmployeeData()]);
                    },
                    onError: async () => {
                        toast.current?.show({
                            severity: "error",
                            summary: "Erro",
                            detail: "Erro ao cancelar as férias.",
                            life: 3500,
                        });
                        await refetch();
                    },
                });
            } else if (vacation.status === VacationStatusEnum.APROVADO) {
                const updated: Vacation = {
                    ...vacation,
                    status: VacationStatusEnum.CANCELAMENTO_PENDENTE,
                    rejectionReason: undefined,
                };

                updateVacation(updated, {
                    onSuccess: async () => {
                        toast.current?.show({
                            severity: "success",
                            summary: "Cancelamento Pendente",
                            detail: "Pedido de cancelamento efetuado com sucesso!",
                            life: 3500,
                        });
                        await Promise.all([refetch(), refetchEmployeeData()]);
                    },
                    onError: async () => {
                        toast.current?.show({
                            severity: "error",
                            summary: "Erro",
                            detail: "Erro ao cancelar as férias.",
                            life: 3500,
                        });
                        await refetch();
                    },
                });
            } else {
                toast.current?.show({
                    severity: "info",
                    summary: "Informação",
                    detail: `Não é possível cancelar férias no estado "${vacation.status}".`,
                    life: 3500,
                });
            }
        };

        confirmAction("Deseja cancelar estas férias?", performCancel);
    };

    const handleScheduleClick = () => {
        setShowScheduleModal(true);
    };

    if (isLoading || isCheckingPermission) {
        return <VacationSkeleton />;
    }

    return (
        <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
            {/* ========== ABAS DE FILTRO REUTILIZÁVEIS ========== */}
            <MobileFilterTabs
                tabs={filterTabs}
                activeTab={activeTab}
                onChange={setActiveTab}
                scrollable
            />

            {/* ========== TÍTULO DA SECÇÃO DOS PEDIDOS (SEM UPPERCASE) ========== */}
            <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                <span
                    className="font-semibold text-xs"
                    style={{ color: "#64748B" }}
                >
                    Os meus pedidos
                </span>

                {isRefetching ? (
                    <MobileRefreshingIndicator />
                ) : (
                    <span className="text-xs" style={{ color: "#94A3B8" }}>
                        {filteredVacations.length} {filteredVacations.length === 1 ? "registo" : "registos"}
                    </span>
                )}

            </div>

            {/* ========== LISTA DE CARTÕES COM EXPANSÃO ========== */}
            <MobileRefreshingList
                isRefreshing={isRefetching}
                className="w-full flex flex-column justify-content-center gap-3"
            >
                {filteredVacations.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-calendar text-4xl" style={{ color: "#CBD5E1" }} />
                        <span className="text-sm text-center" style={{ color: "#64748B" }}>
                            {activeTab !== "all"
                                ? "Nenhum pedido encontrado com este filtro."
                                : "Sem pedidos de férias registados."}
                        </span>
                    </div>
                ) : (
                    filteredVacations.map((vacation) => (
                        <VacationCard
                            key={vacation.id}
                            vacation={vacation}
                            isExpanded={expandedVacationId === vacation.id}
                            onToggle={() =>
                                setExpandedVacationId((prev) =>
                                    prev === vacation.id ? null : vacation.id ?? null
                                )
                            }
                            onCancel={handleCancel}
                        />
                    ))
                )}
            </MobileRefreshingList>

            {/* Botão Flutuante Circular de Agendar (+) */}
            <Button
                icon="pi pi-plus"
                rounded
                className="fixed"
                onClick={handleScheduleClick}
                style={{
                    bottom: "6rem",
                    right: "20px",
                    zIndex: 1000,
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#0084D1",
                    border: "none",
                }}
            />

            <ScheduleVacationModal
                visible={showScheduleModal}
                setVisible={setShowScheduleModal}
                toastRef={toast}
                refetch={refetch}
                refetchEmployeeData={refetchEmployeeData}
                vacationDaysCurrentYear={userEmployee?.vacationDaysCurrentYear ?? 0}
                vacationDaysLastYear={userEmployee?.vacationDaysLastYear ?? 0}
                vacationDaysNextYear={userEmployee?.vacationDaysNextYear}
                vacationDaysNextYearFromLastYear={userEmployee?.vacationDaysNextYearFromLastYear}
                maxVacationDaysNextYear={maxVacationDaysNextYear}
            />

            <ConfirmDialog />
            <MobileLoadingOverlay visible={isDeleting || isUpdating} message="A cancelar férias..." />
            <MobileToast ref={toast} />
        </div >
    );
};

export default Vacations;